import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo } from 'react';
import { FieldValues, Resolver } from 'react-hook-form';
import { FormContainer, FormContainerProps } from 'react-hook-form-mui';
import { ZodTypeAny } from 'zod';
import { EnterToSubmitMode, useEnterToSubmit } from './hooks/useEnterToSubmit';
import { EnterToSubmitContext } from './hooks/useEnterToSubmitContext';

export type AppFormProps<TFieldValues extends FieldValues = FieldValues> = Omit<
  FormContainerProps<TFieldValues>,
  'resolver'
> & {
  /** Zod schema used to build the form resolver. */
  schema?: ZodTypeAny;
  /**
   * Enter-to-submit behaviour.
   * - `auto` (default): Ctrl/Cmd+Enter when the form has a textarea, otherwise Enter
   * - `simple`: Enter submits
   * - `combined`: Ctrl/Cmd+Enter submits
   * - `false`: disabled
   */
  enterToSubmit?: EnterToSubmitMode;
  /** When false, enter-to-submit shortcuts do not submit. Defaults to true. */
  submitEnabled?: boolean;
};

/**
 * App-wide form wrapper around FormContainer.
 * Accepts a zod schema (instead of a resolver) and supports Enter-to-submit.
 *
 * Note: when `formContext` is provided, FormContainer ignores `resolver`.
 * In that case pass the schema to `useForm({ resolver: zodResolver(schema) })` instead,
 * or omit `formContext` and let AppForm create the form from `schema` + `defaultValues`.
 */
export function AppForm<TFieldValues extends FieldValues = FieldValues>({
  schema,
  enterToSubmit = 'auto',
  submitEnabled = true,
  FormProps,
  ...props
}: AppFormProps<TFieldValues>) {
  const handleEnterToSubmit = useEnterToSubmit({ mode: enterToSubmit, enabled: submitEnabled });
  const enterToSubmitContext = useMemo(
    () => ({ mode: enterToSubmit, enabled: submitEnabled }),
    [enterToSubmit, submitEnabled],
  );

  return (
    <EnterToSubmitContext.Provider value={enterToSubmitContext}>
      <FormContainer
        {...props}
        resolver={schema ? (zodResolver(schema) as Resolver<TFieldValues>) : undefined}
        FormProps={{
          ...FormProps,
          onKeyDown: (event) => {
            FormProps?.onKeyDown?.(event);
            handleEnterToSubmit(event);
          },
        }}
      />
    </EnterToSubmitContext.Provider>
  );
}
